import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import propToolService from '../../../utils/services/propToolService';
import AFL_PLAYERS from '../../../assets/static_data/afl_players.json';
import TeamGuernsey from '../../SnipeHub/Guernsey';
import SubscriptionRequired from '../../common/SubscriptionRequired';

// player_id → static player record (image_src etc.), built once per session.
const PLAYER_INDEX = new Map(AFL_PLAYERS.map((p) => [p.player_id, p]));

function getInitials(name) {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Compact avatar with graceful fallback to initials when no image exists or
// the remote image fails to load.
function RuckPlayerAvatar({ playerId, playerName, size = 26 }) {
    const [errored, setErrored] = useState(false);
    useEffect(() => { setErrored(false); }, [playerId]);

    const record = PLAYER_INDEX.get(playerId);
    const dim = { width: size, height: size };

    if (errored || !record?.image_src) {
        return (
            <div
                style={dim}
                className="rounded-full bg-gradient-to-br from-slate-700 to-slate-800 ring-1 ring-slate-700/60 flex items-center justify-center flex-shrink-0"
            >
                <span className="font-mono font-bold text-emerald-300/90" style={{ fontSize: size * 0.34 }}>
                    {getInitials(playerName)}
                </span>
            </div>
        );
    }

    return (
        <div style={dim} className="rounded-full bg-slate-800 ring-1 ring-slate-700/60 overflow-hidden flex-shrink-0">
            <img
                src={record.image_src}
                alt={playerName}
                width={size}
                height={size}
                loading="lazy"
                onError={() => setErrored(true)}
                className="w-full h-full object-cover object-top"
            />
        </div>
    );
}

// Small square deep-link button into the Prop Tool, matching the pattern used
// in Snipe Feed / Multi Builder. The tooltip is portaled to <body> so the
// grid's horizontal scroll container can't clip it.
function PropToolButton({ player, onClick }) {
    const ref = useRef(null);
    const [tip, setTip] = useState(null);

    const show = useCallback(() => {
        const el = ref.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const maxWidth = 240;
        const left = Math.max(8, Math.min(r.left, window.innerWidth - maxWidth - 8));
        const below = r.bottom + 6;
        const flipUp = below + 36 > window.innerHeight;
        setTip({ left, top: flipUp ? r.top - 6 : below, flipUp, maxWidth });
    }, []);
    const hide = useCallback(() => setTip(null), []);

    const activate = (e) => { e.stopPropagation(); hide(); onClick(); };

    return (
        <span ref={ref} className="inline-flex flex-shrink-0">
            <button
                type="button"
                onClick={activate}
                onMouseEnter={show}
                onMouseLeave={hide}
                onFocus={show}
                onBlur={hide}
                aria-label={`Open Prop Tool - ${player}`}
                className="inline-flex items-center justify-center w-5 h-5 rounded text-sky-400 border border-sky-500/30 hover:bg-sky-500/10 hover:border-sky-500/50 hover:text-sky-300 focus:outline-none cursor-pointer transition-all duration-150"
            >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35m1.85-5.15a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
            </button>
            {tip && createPortal(
                <span
                    role="tooltip"
                    style={{
                        position: 'fixed',
                        left: tip.left,
                        top: tip.top,
                        maxWidth: tip.maxWidth,
                        transform: tip.flipUp ? 'translateY(-100%)' : 'none',
                        zIndex: 9999,
                    }}
                    className="pointer-events-none px-2.5 py-1.5 rounded-lg
                        bg-slate-900/95 backdrop-blur border border-slate-700/60 shadow-xl shadow-black/40
                        whitespace-nowrap normal-case tracking-normal animate-fade-in"
                >
                    <span className="block text-[11px] font-mono font-normal text-slate-200">
                        Open Prop Tool <span className="text-slate-500">-</span> {player}
                    </span>
                </span>,
                document.body
            )}
        </span>
    );
}

// "Jump to this player's markets" button — pushes the player into the Game
// Market Overview search on the match page. Same portaled-tooltip pattern as
// PropToolButton so it never clips inside the scroll container.
function FindMarketsButton({ player, onClick }) {
    const ref = useRef(null);
    const [tip, setTip] = useState(null);

    const show = useCallback(() => {
        const el = ref.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const maxWidth = 240;
        const left = Math.max(8, Math.min(r.left, window.innerWidth - maxWidth - 8));
        const below = r.bottom + 6;
        const flipUp = below + 36 > window.innerHeight;
        setTip({ left, top: flipUp ? r.top - 6 : below, flipUp, maxWidth });
    }, []);
    const hide = useCallback(() => setTip(null), []);

    const activate = (e) => { e.stopPropagation(); hide(); onClick(); };

    return (
        <span ref={ref} className="inline-flex flex-shrink-0">
            <button
                type="button"
                onClick={activate}
                onMouseEnter={show}
                onMouseLeave={hide}
                onFocus={show}
                onBlur={hide}
                aria-label={`Find markets for ${player}`}
                className="inline-flex items-center justify-center w-5 h-5 rounded text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/10 hover:border-emerald-500/50 hover:text-emerald-300 focus:outline-none cursor-pointer transition-all duration-150"
            >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 4v6a2 2 0 002 2h10m0 0l-4-4m4 4l-4 4" />
                </svg>
            </button>
            {tip && createPortal(
                <span
                    role="tooltip"
                    style={{
                        position: 'fixed',
                        left: tip.left,
                        top: tip.top,
                        maxWidth: tip.maxWidth,
                        transform: tip.flipUp ? 'translateY(-100%)' : 'none',
                        zIndex: 9999,
                    }}
                    className="pointer-events-none px-2.5 py-1.5 rounded-lg
                        bg-slate-900/95 backdrop-blur border border-slate-700/60 shadow-xl shadow-black/40
                        whitespace-nowrap normal-case tracking-normal animate-fade-in"
                >
                    <span className="block text-[11px] font-mono font-normal text-slate-200">
                        Find markets <span className="text-slate-500">-</span> {player}
                    </span>
                </span>,
                document.body
            )}
        </span>
    );
}

// Heat bands: high involvement is green and deepens as it climbs; moderate
// involvement keeps a distinct blue; low/zero values stay plain.
function cellClasses(value) {
    if (value >= 80) return 'bg-emerald-600/30 text-emerald-200 font-semibold';
    if (value >= 60) return 'bg-emerald-500/20 text-emerald-300 font-semibold';
    if (value >= 40) return 'bg-sky-500/15 text-sky-300 font-semibold';
    if (value > 0) return 'text-slate-400';
    return 'text-slate-600';
}

function roundLabel(round) {
    // Numeric home-and-away rounds get the compact "R" prefix; finals codes
    // (e.g. "EF", "GF") pass through as-is.
    return /^\d+$/.test(round) ? `R${round}` : round;
}

/**
 * Ruck Contest grid for the teams in a match.
 *
 * Fetches `/prop-tool/afl/ruck-contests` once for all supplied teams, then lets
 * the user flip between team tabs and season chips. Rows are players (already
 * AVG-descending from the API), columns are rounds; a missing round key is a
 * blank cell (didn't play) while 0 renders as an actual 0.
 */
function TeamRuckContestsTable({ teams, onFindMarkets }) {
    // Key the fetch on the joined names (not array identity) so callers can
    // pass inline array literals without retriggering requests every render.
    const teamsKey = (Array.isArray(teams) ? teams : [teams]).filter(Boolean).join(',');

    const navigate = useNavigate();
    const location = useLocation();

    const [ruckData, setRuckData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTeam, setActiveTeam] = useState(null);
    const [activeSeason, setActiveSeason] = useState(null);

    // Deep-link into the Prop Tool with the player auto-selected, remembering
    // this page so the tool's back button returns here.
    const openPropTool = useCallback((player) => {
        navigate('/prop-tool', {
            state: {
                playerName: player.player_name,
                from: location.pathname,
            },
        });
    }, [navigate, location.pathname]);

    const fetchRuck = useCallback(async () => {
        if (!teamsKey) {
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        setError(null);

        const result = await propToolService.getAflTeamRuckContests(teamsKey);

        if (result.success) {
            setRuckData(result.data);
            const firstTeam = result.data?.teams?.[0] || null;
            setActiveTeam(firstTeam?.team || null);
            setActiveSeason(firstTeam?.seasons?.[0]?.season ?? null);
        } else {
            setRuckData(null);
            setError(result);
        }
        setIsLoading(false);
    }, [teamsKey]);

    useEffect(() => {
        fetchRuck();
    }, [fetchRuck]);

    const teamEntry = useMemo(
        () => ruckData?.teams?.find((t) => t.team === activeTeam) || null,
        [ruckData, activeTeam]
    );

    const seasonEntry = useMemo(() => {
        if (!teamEntry) return null;
        return teamEntry.seasons?.find((s) => s.season === activeSeason)
            || teamEntry.seasons?.[0]
            || null;
    }, [teamEntry, activeSeason]);

    // When switching team tabs, keep the same season if that team has it,
    // otherwise fall back to the team's most recent season.
    const handleTeamChange = (team) => {
        setActiveTeam(team.team);
        const hasSeason = team.seasons?.some((s) => s.season === activeSeason);
        if (!hasSeason) setActiveSeason(team.seasons?.[0]?.season ?? null);
    };

    const rounds = seasonEntry?.rounds || [];
    // Hide players with no ruck involvement at all for the season (total of 0
    // means every game they played registered 0% ruck contests).
    const players = useMemo(
        () => (seasonEntry?.players || []).filter((p) => (p.total ?? 0) > 0),
        [seasonEntry]
    );

    return (
        <section className="rounded-2xl border border-slate-700/50 bg-slate-900/40 p-3 md:p-4">
            {/* Header + team/season switchers */}
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2.5 mb-3">
                <div className="flex items-center gap-2.5">
                    <span className="text-[10px] font-mono uppercase tracking-[0.16em] text-slate-400">Ruck Contests</span>
                    <span className="hidden md:inline text-[10px] font-mono text-slate-600">ruck contest % by round</span>
                </div>

                {!isLoading && !error && ruckData && (
                    <div className="flex flex-wrap items-center gap-2">
                        {/* Team tabs */}
                        {(ruckData.teams || []).length > 1 && (
                            <div className="flex items-center gap-1 p-0.5 rounded-lg bg-slate-950/60 border border-slate-800/70">
                                {ruckData.teams.map((t) => {
                                    const active = t.team === activeTeam;
                                    return (
                                        <button
                                            key={t.team}
                                            type="button"
                                            onClick={() => handleTeamChange(t)}
                                            className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-mono font-semibold transition-all ${
                                                active
                                                    ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30'
                                                    : 'text-slate-400 border border-transparent hover:text-slate-200'
                                            }`}
                                        >
                                            <TeamGuernsey name={t.team} className="w-4 h-4 flex-shrink-0" />
                                            {t.team_abbr || t.team}
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {/* Season chips */}
                        {(teamEntry?.seasons || []).length > 0 && (
                            <div
                                className="flex items-center gap-1 flex-nowrap overflow-x-auto max-w-[220px] md:max-w-none"
                                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                            >
                                {teamEntry.seasons.map((s) => {
                                    const active = s.season === seasonEntry?.season;
                                    return (
                                        <button
                                            key={s.season}
                                            type="button"
                                            onClick={() => setActiveSeason(s.season)}
                                            className={`flex-shrink-0 px-2 py-1 rounded-md text-[11px] font-mono font-medium border transition-all ${
                                                active
                                                    ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40'
                                                    : 'bg-slate-900/50 text-slate-400 border-slate-700/60 hover:text-slate-200 hover:border-slate-600'
                                            }`}
                                        >
                                            {s.season}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {isLoading ? (
                <div className="flex items-center gap-2.5 py-10 justify-center">
                    <div className="w-4 h-4 border-2 border-slate-700 border-t-emerald-400 rounded-full animate-spin" />
                    <span className="text-xs font-mono text-slate-500">Loading ruck contest data…</span>
                </div>
            ) : error ? (
                error.isSubscriptionError ? (
                    <SubscriptionRequired feature="ruck contest data" variant="inline" />
                ) : (
                    <div className="py-10 flex flex-col items-center text-center gap-3">
                        <span className="text-xs font-mono text-slate-500 max-w-sm leading-relaxed">
                            {error.message || 'Unable to load ruck contest data.'}
                        </span>
                        {['RATE_LIMIT_EXCEEDED', 'NETWORK_ERROR', 'SERVER_ERROR', 'SUBSCRIPTION_CHECK_FAILED'].includes(error.errorCode) && (
                            <button
                                type="button"
                                onClick={fetchRuck}
                                className="px-3 py-1.5 text-[11px] font-mono font-medium text-slate-300 border border-slate-700 rounded-lg hover:bg-slate-800 hover:border-slate-600 transition-all"
                            >
                                Retry
                            </button>
                        )}
                    </div>
                )
            ) : players.length === 0 ? (
                <div className="py-10 text-center text-xs font-mono text-slate-500">
                    No ruck contest records for {activeTeam || 'this team'}{seasonEntry ? ` in ${seasonEntry.season}` : ''}.
                </div>
            ) : (
                <>
                    <div className="overflow-x-auto styled-scrollbar rounded-xl border border-slate-800/70">
                        <table className="w-full text-sm border-separate" style={{ borderSpacing: 0 }}>
                            <thead>
                                <tr>
                                    <th className="md:sticky md:left-0 md:z-10 bg-slate-900/95 backdrop-blur text-left text-[10px] font-mono uppercase tracking-[0.14em] text-slate-500 font-medium px-3 py-2.5 border-b border-slate-800/70 min-w-[150px]">
                                        Player
                                    </th>
                                    {rounds.map((r) => (
                                        <th
                                            key={r}
                                            className="px-2 py-2.5 text-center text-[10px] font-mono font-semibold text-slate-400 border-b border-slate-800/70 bg-slate-900/40 whitespace-nowrap min-w-[38px]"
                                        >
                                            {roundLabel(r)}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {players.map((p, rowIdx) => {
                                    const isLast = rowIdx === players.length - 1;
                                    const borderCls = isLast ? '' : 'border-b border-slate-800/40';
                                    return (
                                        <tr key={p.player_id} className="group">
                                            <td className={`md:sticky md:left-0 md:z-10 bg-slate-900/95 backdrop-blur px-3 py-1.5 whitespace-nowrap ${borderCls}`}>
                                                <div className="flex items-center gap-2">
                                                    <RuckPlayerAvatar playerId={p.player_id} playerName={p.player_name} />
                                                    <PropToolButton player={p.player_name} onClick={() => openPropTool(p)} />
                                                    {onFindMarkets && (
                                                        <FindMarketsButton player={p.player_name} onClick={() => onFindMarkets(p.player_name)} />
                                                    )}
                                                    <span className="text-[11px] font-medium text-slate-300 truncate max-w-[130px]">
                                                        {p.player_name}
                                                    </span>
                                                </div>
                                            </td>
                                            {rounds.map((r) => {
                                                const value = p.ruck_contests_by_round?.[r];
                                                const played = value !== undefined && value !== null;
                                                return (
                                                    <td
                                                        key={r}
                                                        className={`px-2 py-1.5 text-center font-mono text-[11px] tabular-nums transition-colors group-hover:bg-slate-800/20 ${borderCls} ${
                                                            played ? cellClasses(value) : ''
                                                        }`}
                                                    >
                                                        {played ? value : ''}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Heat legend */}
                    <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 px-0.5">
                        <span className="text-[9px] font-mono uppercase tracking-wider text-slate-600">Ruck %</span>
                        <span className="flex items-center gap-1.5 text-[10px] font-mono text-slate-500">
                            <span className="w-3 h-3 rounded bg-emerald-600/40 border border-emerald-500/50" /> 80+
                        </span>
                        <span className="flex items-center gap-1.5 text-[10px] font-mono text-slate-500">
                            <span className="w-3 h-3 rounded bg-emerald-500/25 border border-emerald-500/30" /> 60–79
                        </span>
                        <span className="flex items-center gap-1.5 text-[10px] font-mono text-slate-500">
                            <span className="w-3 h-3 rounded bg-sky-500/25 border border-sky-500/30" /> 40–59
                        </span>
                        <span className="text-[10px] font-mono text-slate-600">blank = didn't play</span>
                    </div>
                </>
            )}
        </section>
    );
}

export default TeamRuckContestsTable;
